import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import {
  ScanLine,
  Package,
  MapPin,
  Truck,
  FileText,
  CheckCircle,
  AlertTriangle,
  History,
  Settings,
  Barcode,
  QrCode,
  Info } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
"@/shared/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import { BarcodeScanner } from '@/shared/components/scanner/BarcodeScanner';
import { BarcodeType, SAMPLE_BARCODES } from '@/shared/lib/barcodeParser';
import { cn } from '@/shared/utils';

// Scan workflow configurations
const scanWorkflows = {
  receiving: {
    name: 'Receiving',
    icon: Truck,
    color: 'cyan',
    description: 'Scan NF-e/CT-e to retrieve ASN, then scan items',
    steps: [
    { name: 'Scan Document', scanMode: 'nfe', expectedType: [BarcodeType.NFE_ACCESS_KEY, BarcodeType.CTE_ACCESS_KEY] },
    { name: 'Scan Items', scanMode: 'gs1', expectedType: [BarcodeType.GS1_128, BarcodeType.EAN_13] }]

  },
  putaway: {
    name: 'Put-away',
    icon: MapPin,
    color: 'emerald',
    description: 'Scan location, then scan item to confirm placement',
    steps: [
    { name: 'Scan Location', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] },
    { name: 'Scan Item', scanMode: 'gs1', expectedType: [BarcodeType.GS1_128, BarcodeType.EAN_13] }]

  },
  picking: {
    name: 'Picking',
    icon: Package,
    color: 'blue',
    description: 'Scan pick slip, location, and item to confirm pick',
    steps: [
    { name: 'Scan Pick Slip', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] },
    { name: 'Scan Location', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] },
    { name: 'Scan Item', scanMode: 'gs1', expectedType: [BarcodeType.GS1_128, BarcodeType.EAN_13] }]

  },
  packing: {
    name: 'Packing',
    icon: Package,
    color: 'violet',
    description: 'Rescan items into carton to verify contents',
    steps: [
    { name: 'Scan Order/Tote', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] },
    { name: 'Scan Item', scanMode: 'gs1', expectedType: [BarcodeType.GS1_128, BarcodeType.EAN_13] },
    { name: 'Scan Carton', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] }]

  },
  dispatch: {
    name: 'Dispatch',
    icon: Truck,
    color: 'indigo',
    description: 'Scan cartons/pallets onto vehicle',
    steps: [
    { name: 'Scan Vehicle', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] },
    { name: 'Scan Pallet/Carton', scanMode: 'internal', expectedType: [BarcodeType.INTERNAL] }]

  }
};

// Scan history item component
const ScanHistoryItem = ({ scan, index }) => {const { t } = useTranslation("wms");
  const typeColors = {
    [BarcodeType.GS1_128]: 'bg-blue-100 text-blue-700',
    [BarcodeType.NFE_ACCESS_KEY]: 'bg-emerald-100 text-emerald-700',
    [BarcodeType.INTERNAL]: 'bg-indigo-100 text-indigo-700',
    [BarcodeType.EAN_13]: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-lg border",
      scan.valid ? "border-gray-200 bg-white" : "border-red-200 bg-red-50"
    )}>
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
        scan.valid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
      )}>
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn("text-xs", typeColors[scan.type] || 'bg-gray-100')}>
            {scan.type}
          </Badge>
          {scan.valid ?
          <CheckCircle className="h-4 w-4 text-emerald-500" /> :

          <AlertTriangle className="h-4 w-4 text-red-500" />
          }
        </div>
        <div className="font-mono text-xs text-gray-600 truncate mt-1">
          {scan.raw.substring(0, 40)}{scan.raw.length > 40 ? '...' : ''}
        </div>
        {scan.fields.GTIN &&
        <div className="text-xs text-gray-500 mt-0.5">{t("gtin", "GTIN:")}
          {scan.fields.GTIN.parsed}
          </div>
        }
      </div>
      <div className="text-xs text-gray-400">
        {new Date().toLocaleTimeString()}
      </div>
    </div>);

};

export const BarcodeScannerPage = () => {const { t } = useTranslation("wms");
  const [selectedWorkflow, setSelectedWorkflow] = useState('receiving');
  const [currentStep, setCurrentStep] = useState(0);
  const [scanHistory, setScanHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('workflow');

  const workflow = scanWorkflows[selectedWorkflow];
  const currentStepConfig = workflow.steps[currentStep];

  const handleScan = (result) => {
    // Add to history
    setScanHistory((prev) => [result, ...prev].slice(0, 50));

    // Advance step if valid
    if (result.valid) {
      if (currentStep < workflow.steps.length - 1) {
        setTimeout(() => {
          setCurrentStep((prev) => prev + 1);
        }, 500);
      }
    }
  };

  const resetWorkflow = () => {
    setCurrentStep(0);
    setScanHistory([]);
  };

  const stats = {
    totalScans: scanHistory.length,
    validScans: scanHistory.filter((s) => s.valid).length,
    invalidScans: scanHistory.filter((s) => !s.valid).length
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="barcode-scanner-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("barcodeScanner", "Barcode Scanner")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("gs1128DatamatrixNfe", "GS1-128, DataMatrix, NF-e QR, CT-e, and internal barcode support")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="gap-1 text-sm">
            <Barcode className="h-4 w-4" />{t("gs1128", "GS1-128")}

          </Badge>
          <Badge variant="outline" className="gap-1 text-sm">
            <QrCode className="h-4 w-4" />{t("datamatrix", "DataMatrix")}

          </Badge>
          <Badge variant="outline" className="gap-1 text-sm">
            <FileText className="h-4 w-4" />{t("nfecte", "NF-e/CT-e")}

          </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("totalScans", "Total Scans")}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.totalScans}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("valid", "Valid")}</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{stats.validScans}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("invalid", "Invalid")}</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{stats.invalidScans}</div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="workflow" className="gap-2">
            <ScanLine className="h-4 w-4" />{t("workflowMode", "Workflow Mode")}

          </TabsTrigger>
          <TabsTrigger value="freeform" className="gap-2">
            <Barcode className="h-4 w-4" />{t("freeScan", "Free Scan")}

          </TabsTrigger>
          <TabsTrigger value="reference" className="gap-2">
            <Info className="h-4 w-4" />{t("reference", "Reference")}

          </TabsTrigger>
        </TabsList>

        {/* Workflow Mode */}
        <TabsContent value="workflow">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Workflow Selection */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t("selectWorkflow", "Select Workflow")}</h3>
                <div className="space-y-2">
                  {Object.entries(scanWorkflows).map(([key, wf]) => {
                    const Icon = wf.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => {setSelectedWorkflow(key);setCurrentStep(0);}}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                          selectedWorkflow === key ?
                          `border-${wf.color}-300 bg-${wf.color}-50` :
                          "border-gray-200 hover:border-gray-300"
                        )}>
                        
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          `bg-${wf.color}-100`
                        )}>
                          <Icon className={cn("h-5 w-5", `text-${wf.color}-600`)} />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{wf.name}</div>
                          <div className="text-xs text-gray-500">{wf.steps.length}{t("steps", "steps")}</div>
                        </div>
                      </button>);

                  })}
                </div>
              </div>

              {/* Workflow Steps */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t("workflowSteps", "Workflow Steps")}</h3>
                <div className="space-y-2">
                  {workflow.steps.map((step, idx) =>
                  <div
                    key={idx}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded-lg",
                      idx === currentStep ? "bg-blue-50 border border-blue-200" :
                      idx < currentStep ? "bg-emerald-50" : "bg-gray-50"
                    )}>
                    
                      <div className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                      idx === currentStep ? "bg-blue-500 text-white" :
                      idx < currentStep ? "bg-emerald-500 text-white" : "bg-gray-300 text-gray-600"
                    )}>
                        {idx < currentStep ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                      </div>
                      <span className={cn(
                      "text-sm",
                      idx === currentStep ? "font-medium text-blue-700" :
                      idx < currentStep ? "text-emerald-700" : "text-gray-500"
                    )}>
                        {step.name}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                  onClick={resetWorkflow}>{t("resetWorkflow", "Reset Workflow")}


                </Button>
              </div>
            </div>

            {/* Center: Scanner */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
                    <p className="text-sm text-gray-500">{workflow.description}</p>
                  </div>
                  <Badge variant="outline" className="text-sm">{t("step", "Step")}
                    {currentStep + 1}{t("of", "of")}{workflow.steps.length}
                  </Badge>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-1">{t("current", "Current:")}
                    {currentStepConfig.name}
                  </div>
                  <div className="text-xs text-gray-500">{t("expected", "Expected:")}
                    {currentStepConfig.expectedType.join(' or ')}
                  </div>
                </div>

                <BarcodeScanner
                  onScan={handleScan}
                  scanMode={currentStepConfig.scanMode}
                  showSamples
                  placeholder={`Scan for ${currentStepConfig.name}...`} />
                
              </div>

              {/* Scan History */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{t("scanHistory", "Scan History")}</h3>
                  <Badge variant="secondary">{scanHistory.length}{t("scans", "scans")}</Badge>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {scanHistory.length > 0 ?
                  scanHistory.map((scan, idx) =>
                  <ScanHistoryItem key={idx} scan={scan} index={idx} />
                  ) :

                  <div className="text-center py-8 text-gray-500">
                      <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t("noScansYet", "No scans yet")}</p>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Free Scan Mode */}
        <TabsContent value="freeform">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">{t("freeScanMode", "Free Scan Mode")}</h3>
              <p className="text-sm text-gray-500 mb-4">{t("scanAnyBarcode", "Scan any barcode type. The system will auto-detect and parse.")}

              </p>
              <BarcodeScanner
                onScan={handleScan}
                showSamples
                placeholder="Scan any barcode..." />
              
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t("recentScans", "Recent Scans")}</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {scanHistory.length > 0 ?
                scanHistory.map((scan, idx) =>
                <ScanHistoryItem key={idx} scan={scan} index={idx} />
                ) :

                <div className="text-center py-8 text-gray-500">
                    <ScanLine className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t("scanABarcode", "Scan a barcode to see results")}</p>
                  </div>
                }
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Reference Tab */}
        <TabsContent value="reference">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Supported Formats */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">{t("supportedBarcodeFormats", "Supported Barcode Formats")}</h3>
              <div className="space-y-4">
                <div className="border-b pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Barcode className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">{t("gs1128", "GS1-128")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("linearBarcodeWith", "Linear barcode with Application Identifiers. Supports GTIN, Lot, Expiry, Serial, Weight, SSCC.")}

                  </p>
                </div>
                <div className="border-b pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <QrCode className="h-5 w-5 text-violet-600" />
                    <span className="font-medium">{t("gs1Datamatrix", "GS1 DataMatrix")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("2dMatrixBarcode", "2D matrix barcode with same AI structure as GS1-128. Higher data density.")}

                  </p>
                </div>
                <div className="border-b pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium">{t("nfeNfce", "NF-e / NFC-e")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("brazilianElectronicInvoice", "Brazilian electronic invoice. 44-digit access key with state, CNPJ, invoice number, and check digit.")}

                  </p>
                </div>
                <div className="border-b pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="h-5 w-5 text-cyan-600" />
                    <span className="font-medium">{t("cte", "CT-e")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("brazilianElectronicTransport", "Brazilian electronic transport document. Same structure as NF-e with model 57.")}

                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-5 w-5 text-indigo-600" />
                    <span className="font-medium">{t("internalBarcodes", "Internal Barcodes")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("ezerpInternalCodes", "EzERP internal codes: ASN-YYYY-NNNN, LOC-X-XX-XX, PLT-NNNN, CTN-NNNN")}

                  </p>
                </div>
              </div>
            </div>

            {/* GS1 Application Identifiers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">{t("gs1ApplicationIdentifiers", "GS1 Application Identifiers")}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 font-medium">{t("ai", "AI")}</th>
                      <th className="text-left p-2 font-medium">{t("name", "Name")}</th>
                      <th className="text-left p-2 font-medium">{t("length", "Length")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t"><td className="p-2 font-mono">{t("01", "01")}</td><td className="p-2">{t("gtin", "GTIN")}</td><td className="p-2">{t("14Fixed", "14 fixed")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("10", "10")}</td><td className="p-2">{t("batchlot", "Batch/Lot")}</td><td className="p-2">{t("20Var", "\u226420 var")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("17", "17")}</td><td className="p-2">{t("expiryDate", "Expiry Date")}</td><td className="p-2">{t("6Fixed", "6 fixed")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("21", "21")}</td><td className="p-2">{t("serialNumber", "Serial Number")}</td><td className="p-2">{t("20Var", "\u226420 var")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("00", "00")}</td><td className="p-2">{t("sscc", "SSCC")}</td><td className="p-2">{t("18Fixed", "18 fixed")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("310x", "310x")}</td><td className="p-2">{t("netWeightKg", "Net Weight (kg)")}</td><td className="p-2">{t("6Fixed", "6 fixed")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("37", "37")}</td><td className="p-2">{t("count", "Count")}</td><td className="p-2">{t("8Var", "\u22648 var")}</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">{t("400", "400")}</td><td className="p-2">{t("orderNumber", "Order Number")}</td><td className="p-2">{t("30Var", "\u226430 var")}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>);

};